<template>
  <!--
    Always rendered, even when empty: assistive technology only announces a
    change to a live region that was already in the accessibility tree, so a
    region created at the moment of the message is silent.

    Hiding is delegated to VisuallyHidden rather than repeated here. Atoms may
    compose atoms, so the eight hiding declarations have exactly one owner and
    this file needs no stylesheet of its own. The live-region attributes fall
    through onto the element VisuallyHidden renders.
  -->
  <VisuallyHidden v-bind="liveRegionAttrs">
    {{ message }}
  </VisuallyHidden>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import VisuallyHidden from '../VisuallyHidden/VisuallyHidden.atom.vue'

const props = withDefaults(
  defineProps<{
    /** Text to announce. Already translated by the caller. */
    message: string
    /** `polite` waits for a pause; `assertive` interrupts. */
    politeness?: 'assertive' | 'polite'
    /** `alert` for errors, `status` for progress and confirmations. */
    role?: 'alert' | 'status'
  }>(),
  { politeness: 'polite', role: 'status' }
)

// Bound as a group: these are fallthrough attributes on the element
// VisuallyHidden renders, not part of its declared prop contract.
const liveRegionAttrs = computed(() => ({
  'aria-atomic': 'true',
  'aria-live': props.politeness,
  role: props.role
}))
</script>
